import type { ProviderProps } from '@reactour/tour';
import type React from 'react';
import { useSetupTourRuntime } from './SetupTourContext';
import { useTourTargetReadiness } from './targetReadiness';
import type { SetupTourStep } from './types';

export type TourButtonRenderProps = Parameters<NonNullable<ProviderProps['nextButton']>>[0];

export const SetupTourNextButton = ({
  Button,
  currentStep,
  steps,
}: TourButtonRenderProps): React.JSX.Element | null => {
  const { isTransitioning, next, goToStep } = useSetupTourRuntime();
  const step = steps?.[currentStep] as SetupTourStep | undefined;
  const label = step?.next?.label ?? 'Next';
  const secondary = step?.next?.secondary;
  const isReady = useTourTargetReadiness(step?.next?.guardTarget);
  const isDisabled = isTransitioning || !isReady;

  if (step?.hideNext) return null;

  const secondaryButton = secondary ? (
    <button
      type="button"
      className="setup-tour-secondary-action"
      disabled={isTransitioning}
      onClick={() => void goToStep(secondary.targetStepId)}
    >
      {secondary.label}
    </button>
  ) : null;

  // Rendered as siblings (not nested in one wrapper) so `order` on each can place
  // Finish next to Back and Continue on its own row below the dots - see the
  // `controls`/`navigation`/`button` style overrides in SetupTourProvider.
  if (isDisabled) {
    return (
      <>
        {secondaryButton}
        <span className="setup-tour-next-actions">
          <span
            className="setup-tour-disabled-next"
            aria-disabled="true"
            style={{ cursor: 'not-allowed' }}
          >
            <Button kind="next" onClick={() => undefined}>
              {label}
            </Button>
          </span>
        </span>
      </>
    );
  }

  return (
    <>
      {secondaryButton}
      <span className="setup-tour-next-actions">
        <span
          className="setup-tour-enabled-next"
          aria-disabled="false"
          style={{ cursor: 'pointer' }}
        >
          <Button kind="next" onClick={() => void next()}>
            {label}
          </Button>
        </span>
      </span>
    </>
  );
};

export const SetupTourPreviousButton = ({
  Button,
  currentStep,
  steps,
}: TourButtonRenderProps): React.JSX.Element | null => {
  const { previous } = useSetupTourRuntime();
  const step = steps?.[currentStep] as SetupTourStep | undefined;
  if (currentStep <= 0 || step?.hidePrevious) return null;

  return (
    <Button kind="prev" onClick={() => void previous()}>
      Back
    </Button>
  );
};
